import React, { useState } from 'react';
import { Download, X, Smartphone, Share, PlusSquare, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePWAInstall } from '@/hooks/usePWAInstall';

const InstallAppPrompt = () => {
  const { isInstallable, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem('pwa-install-dismissed') === 'true';
  });
  
  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSModal(true);
    } else {
      const result = await promptInstall();
      if (result.outcome === 'accepted') {
        setDismissed(true);
      }
    }
  };
  
  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };
  
  // Não mostra se já instalado, dismissado, ou não instalável (exceto iOS)
  if (isInstalled || (dismissed && !isIOS)) {
    return null;
  }
  
  // Mostra apenas se for instalável ou for iOS
  if (!isInstallable && !isIOS) {
    return null;
  }
  
  return (
    <>
      {/* Banner de Instalação */}
      <div 
        className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg p-4 mb-6 shadow-lg"
        data-testid="install-app-banner"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Smartphone className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Instale o App Rota 017</h3>
              <p className="text-sm text-orange-100">
                Acesse mais rápido e receba notificações em tempo real
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleInstall}
              className="bg-white text-orange-600 hover:bg-orange-50"
              data-testid="install-app-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Instalar
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="text-white hover:bg-white/20"
              data-testid="dismiss-install-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Modal de instruções para iOS */}
      <Dialog open={showIOSModal} onOpenChange={setShowIOSModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-orange-500" />
              Instalar no iPhone/iPad
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-gray-600 text-sm">
              Para instalar a Rota 017 no seu dispositivo iOS, siga os passos:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="bg-orange-100 text-orange-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <p className="font-medium text-gray-900">Toque no botão Compartilhar</p>
                  <div className="flex items-center gap-1 mt-1">
                    <Share className="h-5 w-5 text-blue-500" />
                    <span className="text-sm text-gray-500">Na barra inferior do Safari</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="bg-orange-100 text-orange-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <p className="font-medium text-gray-900">Selecione "Adicionar à Tela de Início"</p>
                  <div className="flex items-center gap-1 mt-1">
                    <PlusSquare className="h-5 w-5 text-gray-600" />
                    <span className="text-sm text-gray-500">Role para baixo se necessário</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="bg-orange-100 text-orange-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <p className="font-medium text-gray-900">Toque em "Adicionar"</p>
                  <div className="flex items-center gap-1 mt-1">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-gray-500">Pronto! O app aparecerá na sua tela</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button onClick={() => setShowIOSModal(false)} variant="outline">
              Entendi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Componente compacto para o header
export const InstallAppButton = () => {
  const { isInstallable, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [showIOSModal, setShowIOSModal] = useState(false);
  
  if (isInstalled) {
    return null;
  }
  
  if (!isInstallable && !isIOS) {
    return null;
  }
  
  const handleClick = async () => {
    if (isIOS) {
      setShowIOSModal(true);
    } else {
      await promptInstall();
    }
  };
  
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        className="gap-2 border-orange-300 text-orange-600 hover:bg-orange-50"
        data-testid="install-app-header-btn"
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">Instalar App</span>
      </Button>
      
      {/* Modal iOS (mesmo do componente principal) */}
      <Dialog open={showIOSModal} onOpenChange={setShowIOSModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-orange-500" />
              Instalar no iPhone/iPad
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-gray-600 text-sm">
              Para instalar a Rota 017 no seu dispositivo iOS:
            </p>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="bg-orange-100 text-orange-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                <div>
                  <p className="font-medium">Toque em <Share className="h-4 w-4 inline text-blue-500" /> Compartilhar</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="bg-orange-100 text-orange-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                <div>
                  <p className="font-medium">Selecione <PlusSquare className="h-4 w-4 inline" /> Adicionar à Tela de Início</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="bg-orange-100 text-orange-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                <div>
                  <p className="font-medium">Toque em Adicionar</p>
                </div>
              </div>
            </div>
          </div>
          
          <Button onClick={() => setShowIOSModal(false)} variant="outline" className="w-full">
            Entendi
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallAppPrompt;
